import { MouseEvent, KeyboardEvent } from "react";
import classNames from "classnames";
import { Stack } from "@altimateai/lego";
import styles from "./styles.module.scss";
import IconClickable from "./IconClickable";
import {
  ArrowLeftDoubleIcon,
  ArrowLeftIcon,
  ArrowRightDoubleIcon,
  ArrowRightIcon,
} from "../assets/icons";

interface ExpansionLevelControlsProps {
  leftExpansion: number;
  rightExpansion: number;
  maxRange: [number, number];
  /** False until maxRange has been computed; expand arrows read as disabled meanwhile. */
  boundsKnown: boolean;
  onMaxLeft: () => void;
  onExpandLeft: () => void;
  onCollapseLeft: () => void;
  onCollapseRight: () => void;
  onExpandRight: () => void;
  onMaxRight: () => void;
}

const stop = (fn: () => void) => (e: MouseEvent | KeyboardEvent) => {
  e.stopPropagation();
  fn();
};

const AT_MAX_TITLE = "Already showing every level in this direction";
const AT_MIN_TITLE = "Already collapsed to the selected model";

/**
 * Parents/Children expansion level steppers used inside the lineage Expand
 * popover. Outward arrows grow the band one level (or jump to max); inward
 * arrows collapse it back one level.
 *
 * Each arrow is disabled once it can no longer change the level (already at max
 * in that direction, or already collapsed to 0). Without this the arrows keep
 * looking clickable while silently doing nothing, which reads as "the menu is
 * frozen" — the whole popover appears dead once one direction hits its bound.
 */
const ExpansionLevelControls = ({
  leftExpansion,
  rightExpansion,
  maxRange,
  boundsKnown,
  onMaxLeft,
  onExpandLeft,
  onCollapseLeft,
  onCollapseRight,
  onExpandRight,
  onMaxRight,
}: ExpansionLevelControlsProps) => {
  // Bounds come from maxRange (the deepest level that exists in each direction),
  // never from how far the canvas happens to be expanded right now — the stepper
  // edits a pending value, so dialling it below the rendered depth re-enables the
  // expand arrow immediately without another Apply.
  //
  // Until maxRange is known, disable expand: the callbacks clamp against the
  // [0,0] placeholder, so an enabled-looking arrow would silently no-op (or snap
  // to 0) — the exact dead-but-clickable state this control removes. Collapse
  // only depends on the current level, so it stays usable while bounds load.
  const leftAtMax = !boundsKnown || leftExpansion >= maxRange[0];
  const leftAtMin = leftExpansion <= 0;
  const rightAtMax = !boundsKnown || rightExpansion >= maxRange[1];
  const rightAtMin = rightExpansion <= 0;
  const iconClass = (isDisabled: boolean) =>
    classNames(styles.icon, { [styles.icon_disabled]: isDisabled });

  return (
    <Stack direction="row" className="al-w-full" gap="xl" justify="between" align="center">
      <div className={styles.expand_nav}>
        <div className={styles.expand_nav_btn}>
          <IconClickable
            className={iconClass(leftAtMax)}
            onClick={stop(onMaxLeft)}
            testId="expansion-max-left"
            ariaLabel="Expand parents to max"
            disabled={leftAtMax}
            title={leftAtMax ? AT_MAX_TITLE : undefined}
          >
            <ArrowLeftDoubleIcon />
          </IconClickable>
          <div className={styles.divider} />
          <IconClickable
            className={iconClass(leftAtMax)}
            onClick={stop(onExpandLeft)}
            testId="expansion-expand-left"
            ariaLabel="Expand parents one level"
            disabled={leftAtMax}
            title={leftAtMax ? AT_MAX_TITLE : undefined}
          >
            <ArrowLeftIcon />
          </IconClickable>
          <div className={styles.divider} />
          <IconClickable
            className={iconClass(leftAtMin)}
            onClick={stop(onCollapseLeft)}
            testId="expansion-collapse-left"
            ariaLabel="Collapse parents one level"
            disabled={leftAtMin}
            title={leftAtMin ? AT_MIN_TITLE : undefined}
          >
            <ArrowRightIcon />
          </IconClickable>
        </div>
        <div className="text-blue al-px-2 al-py-1">{leftExpansion}</div>
      </div>
      <div className={styles.expand_nav}>
        <div className="text-blue al-px-2 al-py-1">{rightExpansion}</div>
        <div className={styles.expand_nav_btn}>
          <IconClickable
            className={iconClass(rightAtMin)}
            onClick={stop(onCollapseRight)}
            testId="expansion-collapse-right"
            ariaLabel="Collapse children one level"
            disabled={rightAtMin}
            title={rightAtMin ? AT_MIN_TITLE : undefined}
          >
            <ArrowLeftIcon />
          </IconClickable>
          <div className={styles.divider} />
          <IconClickable
            className={iconClass(rightAtMax)}
            onClick={stop(onExpandRight)}
            testId="expansion-expand-right"
            ariaLabel="Expand children one level"
            disabled={rightAtMax}
            title={rightAtMax ? AT_MAX_TITLE : undefined}
          >
            <ArrowRightIcon />
          </IconClickable>
          <div className={styles.divider} />
          <IconClickable
            className={iconClass(rightAtMax)}
            onClick={stop(onMaxRight)}
            testId="expansion-max-right"
            ariaLabel="Expand children to max"
            disabled={rightAtMax}
            title={rightAtMax ? AT_MAX_TITLE : undefined}
          >
            <ArrowRightDoubleIcon />
          </IconClickable>
        </div>
      </div>
    </Stack>
  );
};

export default ExpansionLevelControls;
